import React, { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";

interface ReportParams {
  category: string;
  country: string;
  schoolCode: string;
  familyCode: string;
  login: string;
  yr: string;
  reportNo: string;
  calledFromELW: string;
  lName: string;
  fName: string;
  showInline: string;
}

type LoadState = "idle" | "loading" | "loaded" | "error";

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

const REPORT_ENDPOINT = `${API_BASE_URL}api/reports/brick-wall-knowledge-gap/`;

function getClientHeight(): number {
  return (
    window.innerHeight ||
    document.documentElement.clientHeight ||
    document.body.clientHeight ||
    600
  );
}

/** Build query string from report params */
function buildQueryString(params: ReportParams): string {
  const qs = new URLSearchParams();
  if (params.category) qs.set("Category", params.category);
  if (params.country) qs.set("Country", params.country);
  if (params.schoolCode) qs.set("SchoolCode", params.schoolCode);
  if (params.familyCode) qs.set("FamilyCode", params.familyCode);
  if (params.login) qs.set("Login", params.login);
  if (params.yr) qs.set("Yr", params.yr);
  if (params.reportNo) qs.set("ReportNo", params.reportNo);
  if (params.calledFromELW) qs.set("CalledFromELW", params.calledFromELW);
  if (params.lName) qs.set("LName", params.lName);
  if (params.fName) qs.set("FName", params.fName);
  if (params.showInline) qs.set("ShowInline", params.showInline);
  return qs.toString();
}

function validateParams(params: ReportParams): string | null {
  if (!params.schoolCode) return "SchoolCode is required.";
  if (!params.login) return "Login is required.";
  if (!params.familyCode) return "FamilyCode is required.";
  const yr = parseInt(params.yr, 10);
  if (isNaN(yr) || yr < 0 || yr > 10)
    return "Yr must be a number between 0 and 10.";
  return null;
}

const Spinner: React.FC = () => (
  <div style={styles.spinnerWrapper} role="status" aria-live="polite">
    <div style={styles.spinnerRing} />
    <p style={styles.spinnerText}>Generating report, please wait…</p>
  </div>
);

const ErrorBanner: React.FC<{ message: string; onRetry: () => void }> = ({
  message,
  onRetry,
}) => (
  <div style={styles.errorBanner} role="alert">
    <span style={styles.errorIcon}>⚠</span>
    <span style={styles.errorText}>{message}</span>
    <button style={styles.retryBtn} onClick={onRetry}>
      Retry
    </button>
  </div>
);

const BrickWallKnowledgeGapReport: React.FC = () => {
  const [searchParams] = useSearchParams();

  // Parse all query params (mirrors Request.QueryString in VB Page_Load)
  const reportParams: ReportParams = {
    category: searchParams.get("Category") ?? "",
    country: searchParams.get("Country") ?? "",
    schoolCode: searchParams.get("SchoolCode") ?? "",
    familyCode: searchParams.get("FamilyCode") ?? "",
    login: searchParams.get("Login") ?? "",
    yr: searchParams.get("Yr") ?? "0",
    reportNo: searchParams.get("ReportNo") ?? "0",
    calledFromELW: searchParams.get("CalledFromELW") ?? "",
    lName: searchParams.get("LName") ?? "",
    fName: searchParams.get("FName") ?? "",
    showInline: searchParams.get("ShowInline") ?? "",
  };

  const [loadState, setLoadState] = useState<LoadState>("idle");
  const [pdfObjectUrl, setPdfObjectUrl] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [iframeHeight, setIframeHeight] = useState<number>(600);

  const embedRef = useRef<HTMLEmbedElement>(null);
  const retryCountRef = useRef<number>(0);

  // Compute iframe height (mirrors fn_OnLoad logic in aspx with gfn_GetClientHeight)
  useEffect(() => {
    const updateHeight = () => {
      setIframeHeight(getClientHeight() - 35);
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  // Revoke object URL on unmount to free memory
  useEffect(() => {
    return () => {
      if (pdfObjectUrl) URL.revokeObjectURL(pdfObjectUrl);
    };
  }, [pdfObjectUrl]);

  const fetchReport = useCallback(async () => {
    // Validate params first
    const validationError = validateParams(reportParams);
    if (validationError) {
      setErrorMessage(validationError);
      setLoadState("error");
      return;
    }

    setLoadState("loading");
    setErrorMessage("");

    const qs = buildQueryString(reportParams);
    const url = `${REPORT_ENDPOINT}?${qs}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/pdf",
        },
        credentials: "include", // send session cookies (mirrors ASP.NET session)
      });

      if (!response.ok) {
        let detail = `HTTP ${response.status}`;
        try {
          const body = await response.json();
          detail = body.error ?? detail;
        } catch {
          // ignore JSON parse failure
        }
        throw new Error(`Report generation failed: ${detail}`);
      }

      const contentType = response.headers.get("Content-Type") ?? "";
      if (!contentType.includes("application/pdf")) {
        throw new Error("Unexpected response type. Expected PDF.");
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      // Release previous blob URL
      setPdfObjectUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return objectUrl;
      });

      setLoadState("loaded");
      retryCountRef.current = 0;
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : "An unexpected error occurred.";
      setErrorMessage(msg);
      setLoadState("error");
    }
  }, [reportParams]);

  // Auto-fetch on mount (mirrors Page_Load → Response.BinaryWrite pattern)
  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Download handler
  const handleDownload = () => {
    if (!pdfObjectUrl) return;
    const a = document.createElement("a");
    a.href = pdfObjectUrl;
    const studentName =
      (reportParams.fName || reportParams.login || "student").replace(
        /\s+/g,
        "_"
      );
    a.download = `${studentName}_${reportParams.reportNo}_BrickWallReportAndKnowledge.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div style={styles.container}>
      {/* Toolbar */}
      {loadState === "loaded" && pdfObjectUrl && (
        <div style={styles.toolbar}>
          <span style={styles.toolbarTitle}>
            Brick Wall &amp; Knowledge Gap Report
          </span>
          <div style={styles.toolbarActions}>
            <button
              style={styles.downloadBtn}
              onClick={handleDownload}
              title="Download PDF"
            >
              ⬇ Download
            </button>
            <button
              style={styles.refreshBtn}
              onClick={fetchReport}
              title="Regenerate report"
            >
              ↻ Refresh
            </button>
          </div>
        </div>
      )}

      {/* Loading state */}
      {loadState === "loading" && <Spinner />}

      {/* Error state */}
      {loadState === "error" && (
        <ErrorBanner message={errorMessage} onRetry={fetchReport} />
      )}

      {loadState === "loaded" && pdfObjectUrl && (
        <embed
          ref={embedRef}
          src={pdfObjectUrl}
          type="application/pdf"
          style={{
            border: "none",
            width: "100%",
            height: `${iframeHeight}px`,
            display: "block",
          }}
          title="Brick Wall and Knowledge Gap Report"
          aria-label="Brick Wall and Knowledge Gap Report PDF viewer"
        />
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    width: "100%",
    overflow: "hidden",
    fontFamily: "'Segoe UI', Arial, sans-serif",
    backgroundColor: "#f4f4f4",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "6px 16px",
    backgroundColor: "#1a3a5c",
    color: "#ffffff",
    height: "36px",
    flexShrink: 0,
    boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
  },
  toolbarTitle: {
    fontSize: "14px",
    fontWeight: 600,
    letterSpacing: "0.3px",
  },
  toolbarActions: {
    display: "flex",
    gap: "8px",
  },
  downloadBtn: {
    backgroundColor: "#2e7d32",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    padding: "4px 12px",
    fontSize: "12px",
    cursor: "pointer",
    fontWeight: 600,
  },
  refreshBtn: {
    backgroundColor: "#1565c0",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    padding: "4px 12px",
    fontSize: "12px",
    cursor: "pointer",
    fontWeight: 600,
  },
  spinnerWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
    height: "calc(100vh - 36px)",
    gap: "16px",
  },
  spinnerRing: {
    width: "52px",
    height: "52px",
    border: "5px solid #e0e0e0",
    borderTop: "5px solid #1565c0",
    borderRadius: "50%",
    animation: "spin 0.9s linear infinite",
  },
  spinnerText: {
    fontSize: "15px",
    color: "#555",
    margin: 0,
  },
  errorBanner: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    margin: "32px auto",
    maxWidth: "560px",
    backgroundColor: "#fff3f3",
    border: "1px solid #e57373",
    borderRadius: "6px",
    padding: "16px 20px",
  },
  errorIcon: {
    fontSize: "22px",
    color: "#c62828",
    flexShrink: 0,
  },
  errorText: {
    flex: 1,
    fontSize: "14px",
    color: "#c62828",
    lineHeight: 1.5,
  },
  retryBtn: {
    backgroundColor: "#c62828",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    padding: "6px 16px",
    fontSize: "13px",
    cursor: "pointer",
    fontWeight: 600,
    flexShrink: 0,
  },
};

// Inject CSS keyframe for spinner (only once)
if (typeof document !== "undefined") {
  const styleId = "brick-report-spinner-style";
  if (!document.getElementById(styleId)) {
    const styleTag = document.createElement("style");
    styleTag.id = styleId;
    styleTag.textContent = `@keyframes spin { to { transform: rotate(360deg); } }`;
    document.head.appendChild(styleTag);
  }
}

export default BrickWallKnowledgeGapReport;


// Frontend (React)
//     ↓
// Build URL with params
//     ↓
// Call Django API
//     ↓
// Backend generates PDF
//     ↓
// Return PDF (binary)
//     ↓
// Frontend displays it